import React, { useMemo } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { CalendarDays, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  formatIsoDateDanish,
  resolveActivationDateStatuses,
  resolveSelectedActivationDate,
} from "@/lib/consolidationVersion";
import DocumentVersionStatusBadge from "@/components/document/DocumentVersionStatusBadge";
import { toolbarTier } from "@/partials/document-renderer/components/document-toolbar/documentToolbarTiers";
import {
  DOCUMENT_VERSION_STATUS_LABELS,
  type DocumentVersionStatus,
} from "@/models/document/DocumentVersion";

/**
 * A repealed or superseded law already carries the "Ikke-gældende" watermark,
 * so nothing in it is in force and calling its newest text "aktuel" would
 * contradict the page. It is still the law's latest version, which is what
 * the dropdown is about.
 */
const inForceStatus = (
  status: DocumentVersionStatus,
  isHistoric: boolean
): DocumentVersionStatus =>
  status === "current" && isHistoric ? "latest" : status;

type ConsolidationVersionSelectProps = {
  /** Distinct activation dates (ISO `yyyy-MM-dd`), sorted ascending. */
  activationDates: string[];
  /**
   * Priority-ordered candidates for the currently rendered date (ISO
   * `yyyy-MM-dd`); the first one matching an activation date is selected.
   * Falls back to the latest activation date when none match.
   */
  selectedDateCandidates: ReadonlyArray<string | null>;
  onSelectDate: (date: string) => void;
  /** The law itself is no longer in force (it carries the historic watermark). */
  isHistoric?: boolean;
  className?: string;
};

/**
 * Activation-date dropdown in the viewer toolbar listing every version of the
 * consolidation (newest first). Selecting a date re-renders the document as it
 * stood on that date via `?as_of=`. It is one segment of the toolbar's
 * "Dokument" group, so it renders its trigger and its status badge as
 * siblings rather than wrapping them.
 *
 * A date alone does not tell a reader whether they are on the version in force
 * today, so every entry is labelled with its status and the selection carries
 * a badge saying it outright (LAR-423).
 */
const ConsolidationVersionSelect: React.FC<ConsolidationVersionSelectProps> = ({
  activationDates,
  selectedDateCandidates,
  onSelectDate,
  isHistoric = false,
  className,
}) => {
  const newestFirst = useMemo(
    () => [...activationDates].reverse(),
    [activationDates]
  );
  const value = resolveSelectedActivationDate(
    activationDates,
    selectedDateCandidates
  );
  // Not memoised: the statuses depend on today's date as much as on the dates
  // themselves, and a dependency list cannot see the day roll over.
  const statuses = resolveActivationDateStatuses(activationDates);

  if (!value) return null;

  const selectedStatus = statuses.get(value);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* The calendar icon carries what the word "version:" used to say,
              which buys back the width the label cost. */}
          <Button
            variant="outline"
            size="sm"
            // `shrink` undoes the button base's `shrink-0`: in a group that
            // gives way, a child that cannot shrink paints over its
            // neighbour. The fill is the outline variant's own, which is
            // what every other control in the toolbar carries.
            // Below the compact tier the type indicator and the status badge
            // are hidden but still the group's first and last children, so its
            // rounding lands on them and would leave this one square at both
            // ends.
            className={cn(
              "h-7 min-w-0 shrink gap-1 px-2 text-xs font-medium",
              toolbarTier.capBothWhenCompact,
              className
            )}
            id="consolidation-version-select"
            aria-label="Vælg version af konsolideringen"
          >
            <CalendarDays className="size-3.5 shrink-0 opacity-70" />
            <span className="truncate">{formatIsoDateDanish(value)}</span>
            <ChevronDown className="size-3.5 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48">
          {newestFirst.map((date) => {
            const status = statuses.get(date);
            return (
              <DropdownMenuItem
                key={date}
                onClick={() => onSelectDate(date)}
                className={cn(
                  "text-xs justify-between gap-2",
                  date === value ? "bg-accent text-accent-foreground" : ""
                )}
              >
                {formatIsoDateDanish(date)}
                {status && (
                  // Every entry is marked: leaving older versions blank would
                  // make "older" the one status you have to infer. The marker
                  // dims the item's own colour rather than using the muted
                  // token, which does not follow the highlighted background.
                  <span className="opacity-70">
                    {DOCUMENT_VERSION_STATUS_LABELS[
                      inForceStatus(status, isHistoric)
                    ].short.toLowerCase()}
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedStatus && (
        <DocumentVersionStatusBadge
          status={inForceStatus(selectedStatus, isHistoric)}
          label={
            DOCUMENT_VERSION_STATUS_LABELS[
              inForceStatus(selectedStatus, isHistoric)
            ].short
          }
          // `h-auto` lets the group stretch it to the height of the segment
          // beside it; the badge's own `h-5` would leave a step in the row.
          // Below the medium tier it is what has to give: the dropdown still
          // names the status of every date it lists, including this one.
          className={cn("h-auto", toolbarTier.fromMediumInlineFlex)}
        />
      )}
    </>
  );
};

export default ConsolidationVersionSelect;
